import {BehaviorSubject, Observable} from 'rxjs';
import {EventEmitter}                from '@angular/core';
import {IGridStatus}                 from '../../../ap-interface/interfaces/ap-grid-status.interface';
import {ApDynGridColumnConfigBase}   from '../../../ap-dyngrids/config/ap-dyn-grid-column-config-base';
import {ApDynGridPagerConfigBase}    from '../../../ap-dyngrids/config/ap-dyn-grid-pager-config-base';
import {ApDynformsConfigFieldset}    from '../../../ap-dynforms/config/ap-dynforms-config-fieldset';
import {ApDynComponentComponent}     from '../../../ap-dyncomponent/ap-dyncomponent.component';
import {ApDynformsComponent}         from '../../../ap-dynforms/ap-dynforms.component';
import IGuid = System.IGuid;
import IUnit = Data.Common.IUnit;
import IWorktypes = Data.BaseData.IWorktypes;
import IFieldWorkPlanBook = Data.TaskManagement.IFieldWorkPlanBook;

/**
 * Enum representing different states/pages in the fieldwork planning wizard
 */
export enum FwPlanningWizardStates {
  Cancel = 'Global__Cancel', // user clicks 'cancel' button on Selection page
  Selection = 'Global__Selection', // first wizard page where user can select fields
  Details = 'Global__Details', // second wizard page configuring the details
  Overview = 'Admin_Pages__Overview', // last wizard page showing summary
  Save = 'Global__Save', // user clicks 'save' button on Overview page
  Initial = '' // initial state when the component is loaded
}

/**
 * Base provider interface for wizard functionality
 */
export interface IFwPlanPlanningWizardProvider<T, R> {
  onGridSelectionChanged(items: T[]): void;

  onLeave(): R;

  onDestroy(): void;

  updateCaption(): void;

  updateFieldSets(): void;

  getGridColumnConfig(detailsData: IFwPlanPlanningWizardDetailsData): Observable<ApDynGridColumnConfigBase[]>;

  getPagerConfig(breadCrumbsColumns: FwPlanningWizardStates[]): ApDynGridPagerConfigBase;
}

/**
 * Provider interface for the field selection page
 */
export interface IFwPlanPlanningWizardSelectionProvider {
  init(caption$: BehaviorSubject<string>,
       fieldSets$: BehaviorSubject<ApDynformsConfigFieldset[]>,
       handleStateChange: EventEmitter<FwPlanningWizardStates>,
       dynComponent: ApDynComponentComponent): void;

  buildDataItems(): Observable<IFwPlanPlanningWizardSelectionData[]>;
}

/**
 * Provider interface for the overview page
 */
export interface IFwPlanPlanningWizardOverviewProvider {
  init(caption$: BehaviorSubject<string>,
       fieldSets$: BehaviorSubject<ApDynformsConfigFieldset[]>,
       handleStateChange: EventEmitter<FwPlanningWizardStates>,
       dynComponent: ApDynComponentComponent): void;

  buildDataItems(selectionData: IFwPlanPlanningWizardSelectionData[],
                 detailsData: IFwPlanPlanningWizardDetailsData): Observable<IFwPlanPlanningWizardOverviewData[]>;
}

/**
 * Provider interface for the details page
 */
export interface IFwPlanPlanningWizardDetailsProvider {
  init(caption$: BehaviorSubject<string>,
       fieldSets$: BehaviorSubject<ApDynformsConfigFieldset[]>,
       dynForm: ApDynformsComponent): void;

  buildItem(selectionData: IFwPlanPlanningWizardSelectionData[]): IFwPlanPlanningWizardDetailsData;

  onFormValueChanges(valueChangesArgs: { control: string; value: any }): void;
}

/**
 * Interface for field selection data
 */
export interface IFwPlanPlanningWizardSelectionData {
  Id: IGuid;
  Status: IGridStatus;
  FieldName: string;
  FieldNumber: number | undefined;
  FieldSecondNumber: number | undefined;
  FieldCombinedNumber: string | undefined;
  FieldGeomId: IGuid | undefined;
  Area: number | undefined;
  CropName: string;
}

/**
 * Interface for single edit data fields
 */
export interface IFwPlanPlanningWizardSingleEditData {
  ApplicationDate: Date | undefined;
  Start: Date | undefined;
  Notes: string | undefined;
  Unit: number | undefined;
  Value: number | undefined;
  Machine: IGuid | undefined;
  Driver: IGuid | undefined;
  Instrument: IGuid | undefined;
  HaPerHour: number | undefined;
  DurationInHours: number | undefined;
}

/**
 * Interface for details page data, extends single edit data
 */
export interface IFwPlanPlanningWizardDetailsData extends IFwPlanPlanningWizardSingleEditData {
  CountFields: number;
  TotalArea: string;
  WorkTypeCategory: number;
  WorkType: number | undefined;
}

/**
 * Interface for overview page data
 */
export interface IFwPlanPlanningWizardOverviewData {
  Id: IGuid;
  WorkType: string;
  Start: Date | undefined;
  Unit: string | undefined;
  Driver: string | undefined;
  Machine: string | undefined;
  Instrument: string | undefined;
  DurationInMilliseconds: number | undefined;
  HaPerHour: number | undefined;
  SelectionData: IFwPlanPlanningWizardSelectionData;
  DetailsData: IFwPlanPlanningWizardDetailsData;
  PlanBook: IFieldWorkPlanBook;
}

/**
 * Interface for responsive details data
 */
export interface IFwPlanPlanningWizardDetailsResponsiveData {
  WorkTypes$: BehaviorSubject<IWorktypes[]>;
  WorkTypeUnits$: BehaviorSubject<IUnit[]>;
  WorkTypeUnitsDisabled$: BehaviorSubject<boolean>;
  WorkTypeValueDisabled$: BehaviorSubject<boolean>;
  WorkTypeCategories$: BehaviorSubject<WorkTypeCategory[]>;
}

/**
 * Type definition for work type categories
 */
export type WorkTypeCategory = {
  CategoryId: number;
  CategoryText: string;
};
